import {
  ConfluenceDatabaseCreateCommand,
  ConfluenceDatabaseUpdateCommand,
  type ConfluenceDatabaseUpdateCommandProperties,
  ContentReadyCommand,
  ReadContentCommand,
  ROVO_CHAT_CLIENT_ID,
  RovoStreamAbortedCommand,
  RovoStreamCompletedCommand,
  RovoStreamErroredCommand,
  type ConfluenceCreateDatabaseCommandProperties,
  ContentDestroyCommand,
} from '@atlassian/rovo-content-bridge-api-commands';

import { CONFLUENCE_DATABASE_CLIENT_ID } from '@canvas/databases-bridge-api';
import { isRovoPreview } from '@canvas/frame-api/host-api/host-frame-context';
import { client, createRovoBridgeAPIClient } from '@canvas/rovo-content-bridge-api';

import { bridgeEventEmitter } from './bridge-command-emitter';
import { createDatabase } from './handlers/create-database-handler';
import { editDatabase } from './handlers/edit-database-handler';
import { readDatabaseContent } from './handlers/read-database-content-handler';
import {
  handleStreamCompleted,
  handleStreamErrored,
  handleStreamAborted,
} from './handlers/stream-lifecycle-handlers';

export const initDatabasesRovoContentBridge = (contentId: string) => {
  const clientId = client(`${CONFLUENCE_DATABASE_CLIENT_ID}-${contentId}`);

  const bridge = createRovoBridgeAPIClient(clientId, (on: any) => {
    on(
      ConfluenceDatabaseCreateCommand,
      (params: ConfluenceCreateDatabaseCommandProperties) =>
        createDatabase(params, contentId)
    );
    on(
      ConfluenceDatabaseUpdateCommand,
      (params: ConfluenceDatabaseUpdateCommandProperties) => {
        editDatabase(params, contentId);
      }
    );
    on(ReadContentCommand, () => readDatabaseContent(contentId));
    on(RovoStreamCompletedCommand, handleStreamCompleted);
    on(RovoStreamErroredCommand, handleStreamErrored);
    on(RovoStreamAbortedCommand, handleStreamAborted);
  });

  bridgeEventEmitter.initBridge(bridge);

  const contentContext: 'staging-area' | 'global' = isRovoPreview()
    ? 'staging-area'
    : 'global';

  bridge.send(ContentReadyCommand, {
    receiverId: ROVO_CHAT_CLIENT_ID,
    senderId: clientId,
    contentId,
    contentType: 'database',
    contentContext,
    canProvideContext: true,
  });

  // Ensure we unregister all command handlers when the page is unloaded to prevent
  // memory leaks when navigating
  const cleanupBridge = () => {
    bridge.send(ContentDestroyCommand, {
      receiverId: ROVO_CHAT_CLIENT_ID,
      senderId: clientId,
      contentId,
      contentType: 'database',
      contentContext,
    });
    bridge.teardown(clientId);

    window.removeEventListener('beforeunload', cleanupBridge);
    window.removeEventListener('unload', cleanupBridge);
    window.removeEventListener('pagehide', cleanupBridge);
  };
  window.addEventListener('beforeunload', cleanupBridge, { once: true });
  window.addEventListener('unload', cleanupBridge, { once: true });
  window.addEventListener('pagehide', cleanupBridge, { once: true });

  return { clientId, contentId, bridge };
};
